/**
 * ================================================================
 * UI COMPONENT: NotificationDrawer
 * ================================================================
 *
 * @component        NotificationDrawer
 * @description      Full-height drawer for notifications with scroll-based pagination
 * @category         UI Component
 *
 * @props
 * - isOpen: boolean - Whether the drawer is open
 * - onOpenChange: (open: boolean) => void - Callback when drawer state changes
 * - uid: string - User identifier for fetching notifications
 *
 * @styling
 * - Design system: Uses project's CSS custom properties
 * - Responsive: Mobile-optimized layout
 * - Scrolling: Infinite scroll with smooth animations
 *
 * @accessibility
 * - ARIA labels and roles
 * - Keyboard navigation support
 * - Screen reader friendly content
 *
 * @usage
 * ```tsx
 * <NotificationDrawer
 *   isOpen={isDrawerOpen}
 *   onOpenChange={setIsDrawerOpen}
 *   uid="VJ"
 * />
 * ```
 * ================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, CheckCheck, Loader2, RotateCcw, X } from 'lucide-react';
import { getNotificationRoute } from '@/utils/notification-routes/routes';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NotificationItem } from '@/components/ui/NotificationItem';
import { NotificationTabs } from '@/components/ui/NotificationTabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useNotifications } from '@/hooks/useNotifications';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import type { NotificationStatus } from '@/types/notifications';

interface NotificationDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  uid: string;
}

export function NotificationDrawer({ isOpen, onOpenChange, uid }: NotificationDrawerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // ================================================================
  // NOTIFICATIONS MANAGEMENT
  // ================================================================
  //
  // Handles notification data, pagination, and state management
  // Only fetch data when drawer is open to prevent unnecessary API calls
  // Integrates with both API and real-time socket updates
  // ================================================================

  const {
    activeTab,
    notifications,
    isLoading,
    isFetching,
    // isMarkingAsRead,
    isMarkingAllAsRead,
    error,
    hasMore,
    unreadCount,
    currentPage,
    loadMore,
    changeTab,
    markAsRead,
    markAllAsRead,
    refetch,
  } = useNotifications(uid, {
    initialTab: 'All',
    enabled: isOpen, // Only fetch when drawer is open
  });

  // Socket integration for real-time updates
  const { isConnected: socketConnected } = useNotificationSocket({
    token: sessionStorage.getItem('authToken') || '',
    uid: uid,
    enabled: !!uid && isOpen,
  });

  // ================================================================
  // SCROLL HANDLING
  // ================================================================
  //
  // Infinite scroll implementation with performance optimizations
  // Shows scroll-to-top button when user scrolls down
  // ================================================================

  const handleScroll = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;

    // Show scroll to top button when scrolled down
    setShowScrollTop(scrollTop > 300);

    // Load more when near bottom (100px threshold)
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isFetching) {
      loadMore();
    }
  }, [hasMore, isFetching, loadMore]);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // ================================================================
  // NOTIFICATION ACTIONS
  // ================================================================

  const handleNotificationRead = useCallback(
    async (notificationId: string) => {
      try {
        await markAsRead(notificationId);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [markAsRead],
  );

  const handleRefresh = useCallback(() => {
    refetch();
    scrollToTop();
  }, [refetch, scrollToTop]);

  const handleTabChange = useCallback(
    (tab: NotificationStatus) => {
      changeTab(tab);
      scrollToTop();
    },
    [changeTab, scrollToTop],
  );

  // ================================================================
  // EFFECTS
  // ================================================================

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Reset scroll position when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToTop, 100);
    }
  }, [isOpen, scrollToTop]);

  // ================================================================
  // RENDER HELPERS
  // ================================================================

  const renderHeader = () => (
    <SheetHeader className="space-y-4 border-b border-border pb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell className="h-6 w-6 text-primary" />
            {socketConnected && (
              <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-background bg-green-500" />
            )}
          </div>
          <div>
            <SheetTitle className="text-xl font-bold">Notifications</SheetTitle>
            <SheetDescription>
              {socketConnected ? 'Real-time updates enabled' : 'Connecting...'}
            </SheetDescription>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex w-full items-center justify-between">
          {/* Left Section */}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {notifications.length} total
            </Badge>
            {unreadCount > 0 && (
              <Badge
                variant="default"
                className="bg-red-500 text-xs text-white" // 🔹 Color effect for unread badge
              >
                {unreadCount} unread
              </Badge>
            )}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Link
                to="#"
                onClick={markAllAsRead}
                className={`flex items-center text-xs ${
                  isMarkingAllAsRead
                    ? 'cursor-not-allowed text-gray-400'
                    : 'text-blue-500 hover:text-blue-700'
                }`}
              >
                {isMarkingAllAsRead ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <CheckCheck className="mr-1 h-3 w-3" />
                )}
                Mark All Read
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <NotificationTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        unreadCount={unreadCount}
      />
    </SheetHeader>
  );

  const renderContent = () => {
    // Show skeleton only for initial loading
    if (isLoading && notifications.length === 0) {
      return (
        <div className="space-y-3">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="flex animate-pulse items-start space-x-3 rounded-lg border p-4"
            >
              <div className="h-3 w-3 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 rounded bg-gray-200" />
                <div className="h-3 w-1/2 rounded bg-gray-200" />
                <div className="h-3 w-1/4 rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-full bg-destructive/10 p-3">
            <X className="h-6 w-6 text-destructive" />
          </div>
          <h3 className="mb-2 font-semibold text-foreground">Failed to load notifications</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            There was an error loading your notifications.
          </p>
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RotateCcw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      );
    }

    if (!isLoading && notifications.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-full bg-muted/10 p-4">
            <Bell className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="mb-2 font-semibold text-foreground">
            {activeTab === 'All'
              ? 'No notifications yet'
              : `No ${activeTab.toLowerCase()} notifications`}
          </h3>
          <p className="text-sm text-muted-foreground">
            {activeTab === 'All'
              ? "You'll see notifications here when they arrive."
              : `You have no ${activeTab.toLowerCase()} notifications at the moment.`}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <AnimatePresence mode="wait">
          {notifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.3,
                  delay: Math.min(index * 0.01, 0.1),
                  ease: 'easeOut',
                },
              }}
              exit={{
                opacity: 0,
                scale: 0.95,
                transition: {
                  duration: 0.2,
                  ease: 'easeIn',
                },
              }}
              layout
              transition={{
                layout: {
                  duration: 0.3,
                  ease: 'easeInOut',
                },
              }}
            >
              {isLoading && index === notifications.length - 1 ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : notification.app &&
                notification.feature &&
                notification.app !== 'undefined' &&
                notification.feature !== 'undefined' ? (
                /**
                 * Conditionally link only when app + feature resolve to a valid route.
                 */
                (() => {
                  const route = getNotificationRoute(notification.app, notification.feature);
                  const content = (
                    <NotificationItem
                      notification={notification}
                      onMarkAsRead={handleNotificationRead}
                    />
                  );
                  if (route) {
                    return (
                      <Link
                        to={route}
                        onClick={() => {
                          const isUnread = notification.status === 'UNREAD';
                          if (isUnread && markAsRead) {
                            markAsRead(notification.id);
                          }
                        }}
                        className="block"
                      >
                        {content}
                      </Link>
                    );
                  }
                  return (
                    <div
                      onClick={() => {
                        const isUnread = notification.status === 'UNREAD';
                        if (isUnread && markAsRead) {
                          markAsRead(notification.id);
                        }
                      }}
                      className="block"
                    >
                      {content}
                    </div>
                  );
                })()
              ) : (
                <div
                  onClick={() => {
                    const isUnread = notification.status === 'UNREAD';
                    if (isUnread && markAsRead) {
                      markAsRead(notification.id);
                    }
                  }}
                  className="block"
                >
                  <NotificationItem
                    notification={notification}
                    onMarkAsRead={handleNotificationRead}
                  />
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Loading indicator for pagination */}
        {isFetching && currentPage > 1 && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading more notifications...
            </span>
          </div>
        )}

        {/* End of list indicator */}
        {!hasMore && notifications.length > 0 && (
          <div className="border-t border-border py-6 text-center text-sm text-muted-foreground">
            You've reached the end of your notifications
          </div>
        )}
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:w-[420px] md:w-[480px]">
        {/* Header */}
        <div className="flex-shrink-0 p-6 pb-1">{renderHeader()}</div>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 px-6">
          <div ref={scrollRef} className="pb-6">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0.8 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {renderContent()}
            </motion.div>
          </div>
        </ScrollArea>

        {/* Scroll to top button */}
        <AnimatePresence>
          {showScrollTop && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute bottom-6 right-6"
            >
              <Button onClick={scrollToTop} size="sm" className="h-10 w-10 rounded-full shadow-lg">
                ↑
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </SheetContent>
    </Sheet>
  );
}

/**
 * ================================================================
 * END OF COMPONENT: NotificationDrawer
 * ================================================================
 *
 * @exports          NotificationDrawer - Full-featured notification drawer
 * @dependencies     Sheet, ScrollArea, NotificationItem, NotificationTabs
 * @usedBy           Header component, navigation components
 *
 * @features
 * - Infinite scroll: Automatically loads more notifications
 * - Real-time updates: Socket integration for live notifications
 * - Tab filtering: All, Unread, Read notification views
 * - Bulk actions: Mark all as read functionality
 * - Responsive design: Mobile-optimized layout
 * - Smooth animations: Framer Motion integration
 *
 * ================================================================
 */
